// src/pages/CourseList.jsx
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAllCourses, deleteCourse, getLessons } from "../firebase/database";

export default function CourseList() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const data = await getAllCourses();
      // Load lesson counts for each course
      const coursesWithLessons = await Promise.all(
        data.map(async (course) => {
          const lessons = await getLessons(course.id);
          return { ...course, lessonCount: lessons.length };
        })
      );
      setCourses(coursesWithLessons);
    } catch (err) {
      console.error("Error loading courses:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (courseId, courseName) => {
    if (!window.confirm(`Are you sure you want to delete "${courseName}"? This will also delete all lessons.`)) {
      return;
    }
    setDeleteLoading(courseId);
    try {
      await deleteCourse(courseId);
      setCourses(courses.filter(c => c.id !== courseId));
    } catch (err) {
      console.error("Error deleting course:", err);
      alert("Failed to delete course");
    } finally {
      setDeleteLoading(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
        <div style={{ color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>Loading courses...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
            <Link 
              to="/admin" 
              style={{ color: "#6B7280", textDecoration: "none", display: "flex", alignItems: "center" }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: "#1F2937", margin: 0, fontFamily: "DM Sans, sans-serif" }}>
              Course List
            </h1>
          </div>
          <p style={{ color: "#6B7280", margin: "4px 0 0 44px", fontFamily: "DM Sans, sans-serif" }}>
            Manage your courses and lessons
          </p>
        </div>
        <Link
          to="/admin/courses/add"
          style={{
            background: "#3B82F6",
            color: "white",
            padding: "12px 20px",
            borderRadius: 8,
            textDecoration: "none",
            fontWeight: 600,
            fontFamily: "DM Sans, sans-serif",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Add Course
        </Link>
      </div>

      {/* Table */}
      <div style={{ background: "white", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.1)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "DM Sans, sans-serif" }}>
          <thead>
            <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
              <th style={{ padding: "16px 20px", textAlign: "left", fontSize: 13, fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>Course Name</th>
              <th style={{ padding: "16px 20px", textAlign: "center", fontSize: 13, fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>Lessons</th>
              <th style={{ padding: "16px 20px", textAlign: "center", fontSize: 13, fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>Duration (hrs)</th>
              <th style={{ padding: "16px 20px", textAlign: "center", fontSize: 13, fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>Class Hours</th>
              <th style={{ padding: "16px 20px", textAlign: "center", fontSize: 13, fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>Practice Hours</th>
              <th style={{ padding: "16px 20px", textAlign: "right", fontSize: 13, fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>Fees</th>
              <th style={{ padding: "16px 20px", textAlign: "center", fontSize: 13, fontWeight: 600, color: "#6B7280", textTransform: "uppercase" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {courses.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 40, textAlign: "center", color: "#6B7280" }}>
                  No courses found. Click "Add Course" to create one.
                </td>
              </tr>
            ) : (
              courses.map((course, index) => (
                <tr key={course.id} style={{ borderBottom: index < courses.length - 1 ? "1px solid #E5E7EB" : "none" }}>
                  <td style={{ padding: "16px 20px" }}>
                    <span style={{ fontWeight: 600, color: "#1F2937" }}>{course.name}</span>
                  </td>
                  <td style={{ padding: "16px 20px", textAlign: "center" }}>
                    <span style={{ 
                      background: "#EEF2FF", 
                      color: "#4F46E5", 
                      padding: "4px 12px", 
                      borderRadius: 20, 
                      fontSize: 14, 
                      fontWeight: 500 
                    }}>
                      {course.lessonCount || 0}
                    </span>
                  </td>
                  <td style={{ padding: "16px 20px", textAlign: "center", color: "#374151" }}>
                    {course.totalDurationHours || 0} hrs
                  </td>
                  <td style={{ padding: "16px 20px", textAlign: "center", color: "#374151" }}>
                    {course.classHours || 0} hrs
                  </td>
                  <td style={{ padding: "16px 20px", textAlign: "center", color: "#374151" }}>
                    {course.practiceHours || 0} hrs
                  </td>
                  <td style={{ padding: "16px 20px", textAlign: "right", fontWeight: 600, color: "#059669" }}>
                    {formatCurrency(course.fees)}
                  </td>
                  <td style={{ padding: "16px 20px", textAlign: "center" }}>
                    <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                      <Link
                        to={`/admin/courses/${course.id}/edit`}
                        style={{
                          padding: "6px 12px",
                          fontSize: 13,
                          background: "#E5E7EB",
                          color: "#374151",
                          borderRadius: 6,
                          textDecoration: "none",
                          fontWeight: 500,
                        }}
                      >
                        Edit
                      </Link>
                      <Link
                        to={`/admin/courses/${course.id}/lessons`}
                        style={{
                          padding: "6px 12px",
                          fontSize: 13,
                          background: "#EEF2FF",
                          color: "#4F46E5",
                          borderRadius: 6,
                          textDecoration: "none",
                          fontWeight: 500,
                        }}
                      >
                        Lessons
                      </Link>
                      <Link
                        to={`/admin/courses/${course.id}/assign`}
                        style={{
                          padding: "6px 12px",
                          fontSize: 13,
                          background: "#FEF3C7",
                          color: "#D97706",
                          borderRadius: 6,
                          textDecoration: "none",
                          fontWeight: 500,
                        }}
                      >
                        Assign
                      </Link>
                      <Link
                        to={`/admin/courses/${course.id}/progress`}
                        style={{
                          padding: "6px 12px",
                          fontSize: 13,
                          background: "#E0E7FF",
                          color: "#4338CA",
                          borderRadius: 6,
                          textDecoration: "none",
                          fontWeight: 500,
                        }}
                      >
                        Progress
                      </Link>
                      <button
                        onClick={() => handleDelete(course.id, course.name)}
                        disabled={deleteLoading === course.id}
                        style={{
                          padding: "6px 12px",
                          fontSize: 13,
                          background: deleteLoading === course.id ? "#F3F4F6" : "#FEE2E2",
                          color: deleteLoading === course.id ? "#9CA3AF" : "#DC2626",
                          border: "none",
                          borderRadius: 6,
                          cursor: deleteLoading === course.id ? "not-allowed" : "pointer",
                          fontWeight: 500,
                        }}
                      >
                        {deleteLoading === course.id ? "..." : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}